<script setup lang="ts">
import { ref, inject, Ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { getCurrentLocale } from '@/i18n';
import { STATUS_OPEN, STATUS_CLOSED } from '@/constants';
import { useViewComposable } from '@/views/composable';
import { FilterMatchMode, FilterOperator, FilterService } from '@primevue/core/api';
import { formatDate } from '@/utils/dateUtils';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import Select from 'primevue/select';
import InputText from 'primevue/inputtext';
import TableColumn from 'primevue/column';
import Tag from 'primevue/tag';
import ExportExcelFile from '@/components/modal/utils/ExportExcelFile.vue';

const dialogRef = inject('dialogRef') as Ref;
const onApply = dialogRef?.value?.options?.emits?.onApply;
const onClose = dialogRef?.value?.options?.emits?.onClose;
const gateway = dialogRef.value.options.data.gateway;
const sheetData = dialogRef.value.options.data.sheetData;
const type = dialogRef.value.options.data.type;

const currentLanguage = getCurrentLocale();
const { t } = useI18n({ useScope: 'global' });
const gatewayDurationFilter = ref<any>();
const sheetDataDestination = ref<any>([]);
const statuses = ref([STATUS_OPEN, STATUS_CLOSED]);
const sortOrder = ref(0);
const sortField = ref('validateMessage');
const isSort = ref(false);
const { styleColumnExport, renderNameFileExport } = useViewComposable();

const widthList = [10, 12, 12, 10, 12, 12, 20, 25, 15, 10, 20];
const sheetColumnStyle = styleColumnExport(widthList);

FilterService.register('filterDates', (dates, filterValue) => {
  const datesFormat = renderListDateRange(dates);
  return !filterValue || datesFormat.includes(filterValue);
});

const initGatewayDurationFilters = () => {
  gatewayDurationFilter.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    gatewayCode: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    destinationSVId: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    gatewayName: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    destinationName: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    destinationAirportCode: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    additionalDuration: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    cacheDuration: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    expiryDate: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
    travelDate: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: 'filterDates' }] },
    status: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    typeGatewayDuration: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] }
  };
};

initGatewayDurationFilters();

const handleImportFile = () => {
  onApply();
};

const handleRevert = () => {
  onApply();
};

const closeModal = () => {
  onClose();
};

const handleShowMessage = (message: string) => {
  return message ?? t('common.labels.success');
};

const rowClass = (data: any) => {
  return [{ 'bg-error': data?.validateMessage }];
};

const renderDuration = (data: number[]) => {
  return data.reduce((prev, cur) => {
    return !prev ? prev + cur : prev + ', ' + cur;
  }, '');
};

const renderListDateRange = (data: { start: string; end: string }[]) => {
  return data.reduce((prev, cur) => {
    const formatDateString = `${formatDate(cur.start, 'dd/LLL/yyyy', true, currentLanguage)} - ${formatDate(cur.end, 'dd/LLL/yyyy', true, currentLanguage)}`;
    return !prev ? prev + formatDateString : prev + ', ' + formatDateString;
  }, '');
};

const getSeverityStatus = (currentStatus: string) => {
  switch (currentStatus) {
    case STATUS_CLOSED:
      return 'danger';
    case STATUS_OPEN:
      return 'info';
    default:
      return '';
  }
};

const sortMessage = () => {
  isSort.value = !isSort.value;
  sortOrder.value = isSort.value ? -1 : 0;
};

onMounted(() => {
  sheetDataDestination.value = [
    {
      name: 'Sheet 1',
      data: sheetData
    }
  ];
});
</script>
<template>
  <div class="card view-gateway-duration-modal">
    <DataTable v-model:filters="gatewayDurationFilter" :rowClass="rowClass" :value="gateway" paginator showGridlines :rows="10" :sortField="sortField" :sortOrder="sortOrder" dataKey="id" filterDisplay="menu">
      <template #empty> {{ $t('management.no_data_found') }} </template>
      <TableColumn field="gatewayCode" :header="$t('management.package.gateway_code')" :showAddButton="false" :showFilterOperator="false">
        <template #body="{ data, field }">
          <span class="long-text" v-tooltip="data[field]">
            {{ data[field] }}
          </span>
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" class="p-column-filter w-16rem" :placeholder="$t('management.air.search_by_gateway_code')" />
        </template>
      </TableColumn>
      <TableColumn field="gatewayName" :header="$t('management.package.gateway_name')" :showAddButton="false" :showFilterOperator="false">
        <template #body="{ data, field }">
          <div class="flex flex-col" v-tooltip="data[field]">
            <span>{{ data[field] }}</span>
          </div>
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" class="p-column-filter w-16rem" :placeholder="$t('management.air.search_by_gateway_name')" />
        </template>
      </TableColumn>
      <TableColumn field="destinationSVId" :header="$t('management.package.sv_destination_code')" :showAddButton="false" :showFilterOperator="false">
        <template #body="{ data, field }">
          <div class="flex flex-col">
            <span>{{ data[field] }}</span>
          </div>
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" class="p-column-filter w-16rem" :placeholder="$t('management.package.search_by_destination_id')" />
        </template>
      </TableColumn>
      <TableColumn field="destinationAirportCode" :showAddButton="false" :showFilterOperator="false">
        <template #header>
          <div class="flex flex-col white-space-normal">
            <span>{{ $t('management.package.destination') }}</span>
            <span>({{ $t('management.air.airport_code') }})</span>
          </div>
        </template>
        <template #body="{ data, field }">
          <div class="flex flex-col">
            <span>{{ data[field] }}</span>
          </div>
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" class="p-column-filter w-16rem" :placeholder="$t('management.package.search_by_destination_airport_code')" />
        </template>
      </TableColumn>
      <TableColumn field="destinationName" :header="$t('management.package.destination_name')" :showAddButton="false" :showFilterOperator="false">
        <template #body="{ data, field }">
          <div class="flex flex-col">
            <span>{{ data[field] }}</span>
          </div>
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" class="p-column-filter w-16rem" :placeholder="$t('management.package.search_by_destination_name')" />
        </template>
      </TableColumn>
      <TableColumn field="cacheDuration" :header="$t('management.package.cached_duration')" :showAddButton="false" :showFilterOperator="false">
        <template #body="{ data, field }">
          <div class="flex flex-col">
            <span>{{ renderDuration(data[field]) }}</span>
          </div>
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" class="p-column-filter w-16rem" :placeholder="$t('management.package.search_by_cached_duration')" />
        </template>
      </TableColumn>
      <TableColumn field="additionalDuration" :header="$t('management.package.additional_duration')" :showAddButton="false" :showFilterOperator="false">
        <template #body="{ data, field }">
          <div class="flex flex-col">
            <span>{{ renderDuration(data[field]) }}</span>
          </div>
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" class="p-column-filter w-16rem" :placeholder="$t('management.package.search_by_additional_durations')" />
        </template>
      </TableColumn>
      <TableColumn field="travelDate" :header="$t('hotel_contract.travel_date_range')" :showAddButton="false" :showFilterMatchModes="false" :showFilterOperator="false">
        <template #body="{ data, field }">
          <div class="flex flex-col">
            <span>{{ renderListDateRange(data[field]) }}</span>
          </div>
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" class="p-column-filter w-16rem" :placeholder="$t('management.package.search_by_travel_date')" />
        </template>
      </TableColumn>
      <TableColumn field="expiryDate" :header="$t('book_form.expiry_date')" :showAddButton="false" :showFilterOperator="false">
        <template #body="{ data, field }">
          <span>{{ formatDate(data[field], 'dd-LLL-yyyy', true, currentLanguage) }} </span>
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" class="p-column-filter w-16rem" :placeholder="$t('management.package.search_by_expiry_date')" />
        </template>
      </TableColumn>
      <TableColumn field="typeGatewayDuration" :header="$t('management.package.type')" :showAddButton="false" :showFilterOperator="false">
        <template #body="{ data, field }">
          <span>{{ data[field] }} </span>
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" class="p-column-filter w-16rem" />
        </template>
      </TableColumn>
      <TableColumn field="status" :header="$t('common.status')" :showAddButton="false" :showFilterOperator="false">
        <template #body="{ data, field }">
          <span>{{ data[field] }} </span>
        </template>
        <template #filter="{ filterModel }">
          <Select v-model="filterModel.value" :options="statuses" :placeholder="$t('common.select_one')" class="p-column-filter" showClear>
            <template #option="slotProps">
              <Tag :value="slotProps.option" :severity="getSeverityStatus(slotProps.option)" />
            </template>
          </Select>
        </template>
      </TableColumn>
      <TableColumn class="min-w-200" field="validateMessage" :header="$t('management.hotel.message')" :showAddButton="false" :showFilterOperator="false" sortable>
        <template #sorticon>
          <i class="ml-2 sort_icon" :class="sortOrder === -1 ? 'pi pi-sort-amount-down-alt' : 'pi pi-sort-amount-up-alt'" @click="sortMessage()"></i>
        </template>
        <template #body="{ data, field }">
          <p class="mb-0 break-spaces">{{ handleShowMessage(data[field]) }}</p>
        </template>
      </TableColumn>
    </DataTable>
    <div class="flex">
      <div class="w-full text-center" v-if="type === 'IMPORT'">
        <Button icon="pi pi-plus" @click="handleImportFile()" :label="$t('common.buttons.import_file_now')" data-testid="import-btn" class="import-button ml-4" />
      </div>
      <div class="w-full text-center" v-if="type === 'REVERT'">
        <Button id="btn_revert_file" icon="pi pi-plus" @click="handleRevert()" :label="$t('common.labels.revert_to_this_version')" class="revert-button ml-4" />
      </div>
      <div class="w-full text-center" v-if="type === 'EXPORT'">
        <ExportExcelFile id="export_btn" class="export ml-4" :sheets="sheetDataDestination" :columnsStyle="sheetColumnStyle" :file-name="renderNameFileExport('Duration_Rule_Management')"></ExportExcelFile>
      </div>
      <div>
        <Button @click="closeModal()" :label="$t('common.buttons.close')" data-testid="cancel-btn" class="p-button" />
      </div>
    </div>
  </div>
</template>
